// modules/clip-manager-it.js
// Version italiana — importe i18n-it.js pour tous les textes

import { convertToMp3, splitAudioAtTime, generateSplitNames } from './audio-utils.js';
import { i18n } from './traduction-it.js';

export function createAudioClip(blob, fileName, soundClips, updateProgressBar, getIsProMode) {
  const clipContainer = document.createElement('article');
  const clipLabel = document.createElement('p');
  const audio = document.createElement('audio');
  const deleteButton = document.createElement('button');
  const downloadButton = document.createElement('button');
  const moveUpButton = document.createElement('button');
  const moveDownButton = document.createElement('button');
  const splitButton = document.createElement('button');

  let clipName = fileName;
  if (fileName.includes('.')) {
    clipName = fileName.split('.').slice(0, -1).join('.');
  }

  clipContainer.classList.add('clip');
  audio.setAttribute('controls', '');

  // Textes et data-icon italiens
  deleteButton.textContent   = i18n.btnDelete;
  deleteButton.setAttribute('data-icon', 'trash2');
  deleteButton.title         = i18n.btnDelete;
  deleteButton.className     = 'delete';

  downloadButton.textContent = i18n.btnDownload;
  downloadButton.setAttribute('data-icon', 'download');
  downloadButton.title       = i18n.btnDownload;
  downloadButton.className   = 'download';

  moveUpButton.textContent   = i18n.btnMoveUp;
  moveUpButton.setAttribute('data-icon', 'up');
  moveUpButton.title         = i18n.btnMoveUp;
  moveUpButton.className     = 'move-up';

  moveDownButton.textContent = i18n.btnMoveDown;
  moveDownButton.setAttribute('data-icon', 'down');
  moveDownButton.title       = i18n.btnMoveDown;
  moveDownButton.className   = 'move-down';

  splitButton.textContent    = i18n.btnSplit;
  splitButton.setAttribute('data-icon', 'cut2');
  splitButton.title          = i18n.btnSplit;
  splitButton.className      = 'split';

  const processBlob = async () => {
    if (!blob.type.includes('mp3')) {
      const tempAudio = new Audio();
      tempAudio.src = URL.createObjectURL(blob);
      await new Promise(resolve => { tempAudio.onloadedmetadata = resolve; });
      blob = await convertToMp3(tempAudio);
    }
    return blob;
  };

  processBlob().then(processedBlob => {
    audio.src = URL.createObjectURL(processedBlob);
  });

  clipLabel.textContent = clipName || i18n.clipDefaultName;

  setupSplitButton(audio, splitButton, getIsProMode);

  splitButton.addEventListener('click', async () => {
    await handleSplit(audio, clipLabel, splitButton, soundClips, updateProgressBar, getIsProMode);
  });

  const contentContainer = document.createElement('div');
  contentContainer.className = 'clip-content';

  const buttonsContainer = document.createElement('div');
  buttonsContainer.className = 'buttons-container';

  const navigationButtons = document.createElement('div');
  navigationButtons.className = 'navigation-buttons';
  navigationButtons.appendChild(moveUpButton);
  navigationButtons.appendChild(moveDownButton);

  const actionButtons = document.createElement('div');
  actionButtons.className = 'action-buttons';
  actionButtons.appendChild(splitButton);
  actionButtons.appendChild(deleteButton);
  actionButtons.appendChild(downloadButton);

  buttonsContainer.appendChild(navigationButtons);
  buttonsContainer.appendChild(actionButtons);

  contentContainer.appendChild(audio);
  contentContainer.appendChild(buttonsContainer);

  clipContainer.appendChild(clipLabel);
  clipContainer.appendChild(contentContainer);
  soundClips.appendChild(clipContainer);

  setupClipButtons(clipContainer, deleteButton, downloadButton, moveUpButton, moveDownButton, clipLabel, audio, soundClips);

  return clipContainer;
}

function setupSplitButton(audio, splitButton, getIsProMode) {
  splitButton.style.display = 'inline-block';
  splitButton.disabled = true;

  audio.addEventListener('pause', () => {
    splitButton.disabled = false;
    splitButton.dataset.splitTime = audio.currentTime;
  });

  audio.addEventListener('play', () => {
    splitButton.disabled = true;
  });

  audio.addEventListener('ended', () => {
    splitButton.disabled = false;
    splitButton.dataset.splitTime = audio.duration;
  });
}

async function handleSplit(audio, clipLabel, splitButton, soundClips, updateProgressBar, getIsProMode) {
  const splitTime = parseFloat(splitButton.dataset.splitTime);
  const clipContainer = splitButton.closest('.clip');

  try {
    splitButton.disabled = true;
    const [firstPart, secondPart] = await splitAudioAtTime(audio, splitTime, updateProgressBar);
    const baseName = clipLabel.textContent;
    const [firstPartName, secondPartName] = generateSplitNames(baseName);
    createAudioClip(firstPart, `${firstPartName}.mp3`, soundClips, updateProgressBar, getIsProMode);
    createAudioClip(secondPart, `${secondPartName}.mp3`, soundClips, updateProgressBar, getIsProMode);
    clipContainer.remove();
  } catch (error) {
    console.error('Errore durante il taglio:', error);
    alert(i18n.splitError);
  } finally {
    splitButton.disabled = false;
  }
}

function setupClipButtons(clipContainer, deleteButton, downloadButton, moveUpButton, moveDownButton, clipLabel, audio, soundClips) {
  deleteButton.onclick = () => clipContainer.remove();

  clipLabel.onclick = function() {
    const newClipName = prompt(i18n.renamePrompt);
    if (newClipName !== null) {
      this.textContent = newClipName;
    }
  };

  downloadButton.onclick = () => {
    const anchor = document.createElement('a');
    anchor.href = audio.src;
    anchor.download = `${clipLabel.textContent}.mp3`;
    anchor.click();
  };

  moveUpButton.onclick = () => {
    const previousSibling = clipContainer.previousElementSibling;
    if (previousSibling) soundClips.insertBefore(clipContainer, previousSibling);
  };

  moveDownButton.onclick = () => {
    const nextSibling = clipContainer.nextElementSibling;
    if (nextSibling) soundClips.insertBefore(nextSibling, clipContainer);
  };
}

export async function downloadAllClips() {
  const allClips = [
    ...document.querySelectorAll('.sound-clips .clip'),
    ...document.querySelectorAll('.combined-clips .clip')
  ];

  if (allClips.length === 0) {
    alert(i18n.noClipsToDownload);
    return;
  }

  try {
    for (let i = 0; i < allClips.length; i++) {
      const clip = allClips[i];
      const audio = clip.querySelector('audio');
      const label = clip.querySelector('p');
      const clipName = label.textContent || `clip_${i + 1}`;
      const anchor = document.createElement('a');
      anchor.href = audio.src;
      anchor.download = `${clipName}.mp3`;
      anchor.click();
      await new Promise(resolve => setTimeout(resolve, 300));
    }
    alert(i18n.downloadSuccess(allClips.length));
  } catch (error) {
    console.error('Errore durante il download:', error);
    alert(i18n.downloadError);
  }
}